/**
 * The surgeon's decision on an operation — taken by the surgeon, or recorded
 * for them.
 *
 * An operation needs two yeses: the rooms desk's and the surgeon's, and the
 * surgeon's carries the time. Surgeons do not live in the system, though: a
 * patient-reception or Contracts clerk calls them, they agree on the phone,
 * and until now the operation still waited for the surgeon to sign in and
 * press the button. SURGERY_CONFIRM_ON_BEHALF lets that desk record the
 * surgeon's answer itself — with the time the surgeon gave and how they were
 * reached — and the request keeps who recorded it, so the surgeon's panel
 * shows "أكّدها فلان بالنيابة عنك" rather than a click they never made.
 */

import { useCallback, useEffect, useState } from 'react';
import { Button, DateTimeInput, EmptyState, ErrorState, Field, Icon, Select, Spinner } from '../../components/common';
import { requestsApi } from '../../api/endpoints';
import { useNotifications } from '../../context/NotificationContext';
import { usePoll } from '../../hooks/usePolling';
import { NoteDialog } from './NoteDialog';
import { RequestCard } from './RequestCard';
import { CONFIRMATION_CHANNELS } from './labels';

const toLocalInput = (value) => {
  const d = value ? new Date(value) : new Date(Date.now() + 24 * 3600 * 1000);
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

/**
 * The surgeon's yes, with the time — or a no, or a change, with a reason.
 * `onBehalf` is the desk recording it for the surgeon: it also asks how the
 * surgeon was reached.
 */
export function SurgeryDecision({ request, onDone, onBehalf = false }) {
  const { success, error: toastError } = useNotifications();
  const [startAt, setStartAt] = useState(toLocalInput(request.scheduledAt));
  const [endAt, setEndAt] = useState('');
  const [via, setVia] = useState('phone');
  const [busy, setBusy] = useState(false);
  const [noting, setNoting] = useState(null);
  const surgeon = request.doctorName || 'الجراح';

  const decide = async (decision, note = null) => {
    setBusy(true);
    try {
      await requestsApi.surgery(request.id, {
        side: 'doctor',
        decision,
        note,
        via: onBehalf ? via : undefined,
        scheduledAt: decision === 'accepted' ? new Date(startAt).toISOString() : undefined,
        expectedEndAt: decision === 'accepted' && endAt ? new Date(endAt).toISOString() : undefined,
      });
      if (onBehalf) {
        success(decision === 'accepted' ? `تم تسجيل موافقة ${surgeon} وموعد العملية` : `تم تسجيل قرار ${surgeon}`);
      } else {
        success(decision === 'accepted' ? 'تمت موافقتك وتحديد موعد العملية' : 'تم إرسال قرارك');
      }
      setNoting(null);
      onDone?.();
    } catch (err) {
      toastError(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="w-full space-y-3">
      {onBehalf && (
        <div className="on-behalf-strip flex flex-wrap items-center gap-2 rounded-xl px-3 py-2 text-xs">
          <Icon name="phone-call" size={15} className="on-behalf-strip__icon" />
          <span className="min-w-0 flex-1">
            تسجّل قرار <b>{surgeon}</b> بالنيابة عنه بعد التواصل معه — يُحفظ اسمك ووسيلة التواصل مع القرار.
          </span>
        </div>
      )}
      <div className={`grid grid-cols-1 gap-2 ${onBehalf ? 'sm:grid-cols-3' : 'sm:grid-cols-2'}`}>
        <Field label="موعد العملية">
          <DateTimeInput value={startAt} onChange={(e) => setStartAt(e.target.value)} />
        </Field>
        <Field label="الانتهاء المتوقع">
          <DateTimeInput value={endAt} onChange={(e) => setEndAt(e.target.value)} />
        </Field>
        {onBehalf && (
          <Field label="وسيلة التأكيد مع الجراح">
            <Select value={via} onChange={(e) => setVia(e.target.value)}>
              {Object.entries(CONFIRMATION_CHANNELS).map(([id, meta]) => (
                <option key={id} value={id}>
                  {meta.label}
                </option>
              ))}
            </Select>
          </Field>
        )}
      </div>
      <div className="flex flex-wrap gap-2">
        <Button variant="success" icon={onBehalf ? 'phone-call' : 'check'} loading={busy} onClick={() => decide('accepted')}>
          {onBehalf ? 'تأكيد بالنيابة عن الجراح' : 'موافقة وتحديد الموعد'}
        </Button>
        <Button variant="subtle" icon="edit-3" onClick={() => setNoting('modification_requested')}>
          طلب تعديل
        </Button>
        <Button variant="danger" icon="x-circle" onClick={() => setNoting('rejected')}>
          رفض
        </Button>
      </div>
      <NoteDialog
        open={Boolean(noting)}
        onClose={() => setNoting(null)}
        onSubmit={(note) => decide(noting, note)}
        title={noting === 'rejected' ? 'رفض العملية' : 'طلب تعديل على العملية'}
        label={onBehalf ? `ما قاله ${surgeon}` : undefined}
        destructive={noting === 'rejected'}
        confirmLabel="إرسال"
      />
    </div>
  );
}

/** The operations still waiting on their surgeon, for the desks that confirm by phone. */
export function SurgeonConfirmQueue({ onChanged = () => {} }) {
  const [rows, setRows] = useState(null);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    const list = await requestsApi.list({ queue: 'surgeon' });
    setRows(Array.isArray(list) ? list : []);
    setError(null);
  }, []);

  useEffect(() => {
    load().catch(setError);
  }, [load]);
  usePoll(load, 20000, { immediate: false });

  if (error) return <ErrorState error={error} onRetry={() => load().catch(setError)} />;
  if (!rows) return <Spinner label="جارٍ تحميل العمليات" variant="inline" />;
  if (rows.length === 0) {
    return (
      <EmptyState
        icon="scissors"
        title="لا عمليات تنتظر تأكيد الجراح"
        message="تظهر هنا العمليات التي لم يؤكدها الجراح بعد، لتسجيل تأكيده بالنيابة عنه بعد التواصل معه."
      />
    );
  }
  return (
    <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
      {rows.map((r) => (
        <RequestCard key={r.id} request={r}>
          <SurgeryDecision
            request={r}
            onBehalf
            onDone={() => {
              load().catch(setError);
              onChanged();
            }}
          />
        </RequestCard>
      ))}
    </div>
  );
}

export default SurgeryDecision;
